import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { Text } from '@lusha-ui/text';
import { ArrowRight } from '@lusha/icons';
import { PLATFORM } from '@lusha/source-platforms';
import { CheckIcon, InfoDarkIcon, ChevronSmallDownIcon, PeopleFilledIcon } from '@lusha-ui/icons';
import { CircleStar } from '@lusha/icons';
import { getTooltipProps, Tooltip } from '@lusha-ui/tooltip';
import { Button } from '@lusha-ui/button';
import { colors } from '@lusha-ui/theme';
import React, { useMemo } from 'react';
import { BILLING_CYCLES, DEFAULT_EXPANDED_PLAN, PLAN_CODES, STARTER_SEAT_AMOUNT } from '../constants';
import { useAppSelector } from '@reducers/index';
import { selectPlanFeatures, selectStarterPlan, selectStarterPrice } from '@reducers/pricing/selectors';
import { useAnalytics } from '@lusha-engage/analytics';
const StarterPlanCard = ({ billingCycle, isExpanded, onExpand, handleClose }) => {
    var _a;
    const { platform, pricing, openSource } = useAppSelector(state => ({
        platform: state.settings.platform,
        pricing: state.pricing,
        openSource: state.popups.upgradePlan.source,
    }));
    const isMonthly = billingCycle === BILLING_CYCLES.MONTHLY;
    const analytics = useAnalytics();
    const currentPrice = useMemo(() => selectStarterPrice(pricing, isMonthly ? 'month' : 'year'), [isMonthly, pricing.plans]);
    const displayedPrice = useMemo(() => {
        if (!currentPrice) {
            return '$0';
        }
        const rawPrice = isMonthly ? currentPrice.amount : currentPrice.amount / 12;
        const rounded = Math.ceil(rawPrice * 20) / 20;
        return rounded % 1 === 0 ? `$${rounded}` : `$${rounded.toFixed(2)}`;
    }, [currentPrice, isMonthly]);
    const totalCreditsLabel = useMemo(() => {
        var _a;
        if (!currentPrice) {
            return '';
        }
        const credits = currentPrice.totalCredits || ((_a = currentPrice.quota) === null || _a === void 0 ? void 0 : _a.credits) || 0;
        return `${credits} Credits/${isMonthly ? 'month' : 'year'}`;
    }, [currentPrice, isMonthly]);
    const seatsCount = ((_a = currentPrice === null || currentPrice === void 0 ? void 0 : currentPrice.quota) === null || _a === void 0 ? void 0 : _a.seats) || STARTER_SEAT_AMOUNT;
    function handleContinue() {
        const starterPlan = selectStarterPlan(pricing);
        if (!starterPlan || !currentPrice) {
            return;
        }
        const basePriceId = currentPrice.id.replace(/-(?:Monthly|Yearly)-\d+$/, '');
        const params = new URLSearchParams({
            plan: starterPlan.id,
            price: basePriceId,
            source: 'plugin_upgrade_popup',
            sub_platform: platform,
            platform: PLATFORM.PLUGIN,
            epdd: 'false'
        });
        analytics.track({
            eventType: 'pricing_choose_plan_clicked',
            event_value: PLAN_CODES.STARTER,
            event_additional_info: {
                selected_plan: PLAN_CODES.STARTER,
                plugin_click_source: openSource,
                billing_cycle: billingCycle,
                credits: currentPrice.totalCredits,
                seats: seatsCount,
                default_plan: DEFAULT_EXPANDED_PLAN,
            },
        });
        window.open(`${envConfig.BILLING_CHECKOUT_URL}/checkout/v3?${params.toString()}`, '_blank');
        handleClose();
    }
    const tooltipId = 'upgrade-popup-starter-feature-tooltip';
    return (_jsxs("div", { onClick: !isExpanded ? onExpand : undefined, css: css `
        padding: 16px;
        border-radius: 16px;
        border: 1px solid ${isExpanded ? colors.stroke.brand.DEFAULT : colors.semiLightGrey};
        background: ${isExpanded ? colors.background.surface.brand.accent['1'].DEFAULT : colors.white};
        box-shadow: ${isExpanded ? '0 8px 16px 0 rgba(21, 20, 23, 0.1)' : 'none'};
        cursor: ${isExpanded ? 'default' : 'pointer'};
      `, children: [_jsxs("div", { css: css `
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 4px;
        `, children: [_jsx(Text, { variant: "h4-s", children: "Starter" }), _jsx("div", { css: css `
            display: flex;
            align-items: center;
            gap: 8px;
          `, children: _jsxs(Text, { variant: "h3-s", children: [displayedPrice, " /mo."] }) })] }), _jsx("div", { css: css `
          margin-bottom: 8px;
        `, children: _jsx(Text, { variant: "body-2-r", children: isMonthly ? 'Billed monthly' : 'Billed annually' }) }), !isExpanded && (_jsxs("div", { css: css `
              margin-bottom: 8px;
              display: flex;
              align-items: center;
              justify-content: space-between;
            `, children: [_jsxs(Text, { variant: "body-2-r", children: [totalCreditsLabel, " \u00B7 ", seatsCount, " ", seatsCount === 1 ? 'seat' : 'seats'] }), _jsx(ChevronSmallDownIcon, { css: css `
              transition: transform 0.2s ease;
              transform: rotate(${isExpanded ? 180 : 0}deg);
            ` })] })), isExpanded && (_jsxs(_Fragment, { children: [_jsxs("div", { css: css `
              margin-bottom: 8px;
              display: flex;
              align-items: center;
              gap: 8px;
            `, children: [_jsx(CircleStar, { variant: "light", color: "secondary" }), _jsx(Text, { variant: "body-2-r", children: totalCreditsLabel })] }), _jsxs("div", { css: css `
              margin-bottom: 16px;
              display: flex;
              align-items: center;
              gap: 8px;
            `, children: [_jsx(PeopleFilledIcon, { color: colors.text_and_icons.secondary.DEFAULT }), _jsxs(Text, { variant: "body-2-r", children: [seatsCount, " ", seatsCount === 1 ? 'seat' : 'seats', " included"] })] }), _jsxs(Button, { css: css `
              width: 100%;
              margin-bottom: 8px;
            `, textVariant: "body-2-m", onClick: handleContinue, children: ["Continue to Starter ", _jsx(ArrowRight, {})] }), _jsx("div", { css: css `
              margin-bottom: 24px;

              @media only screen and (max-height: 600px) {
                margin-bottom: 12px;
              }
            `, children: _jsx(Text, { variant: "body-3-r", color: colors.text_and_icons.secondary.DEFAULT, children: "Credits granted upfront" }) }), _jsxs("div", { css: css `
              gap: 6px;
              display: flex;
              flex-direction: column;
            `, children: [_jsx(Text, { variant: "body-3-r", children: "Everything in Free, plus:" }), _jsx("div", { css: css `
                gap: 6px;
                display: flex;
                flex-direction: column;
              `, children: selectPlanFeatures(pricing, PLAN_CODES.STARTER).map(({ feature, feature_description }, index) => {
                                    return (_jsxs("div", { css: css `
                      display: flex;
                    `, children: [_jsx(CheckIcon, { css: css `
                        margin-right: 8px;
                      `, color: colors.text_and_icons.positive.DEFAULT }), _jsx(Text, { variant: "body-3-r", color: colors.text_and_icons.secondary.DEFAULT, children: feature }), feature_description ? (_jsxs(_Fragment, { children: [_jsx(InfoDarkIcon, Object.assign({ css: css `
                            margin-left: 6px;
                          ` }, getTooltipProps({
                                                        tooltipId: `${tooltipId}${index}`,
                                                    }))), _jsx(Tooltip, { id: `${tooltipId}${index}`, children: _jsx("div", { css: css `
                              max-width: 200px;
                            `, children: feature_description }) })] })) : ('')] }, index));
                                }) })] })] }))] }));
};
export default StarterPlanCard;
